import type {Organization} from 'crqlar-react-common/dist/models/organization/organization';

// Wall-clock math in an IANA zone with Intl only; times are epoch milliseconds, a "wall" time is the zone's
// clock reading written as if it were UTC.

const DAY_MS = 24 * 60 * 60 * 1000;

const formats = new Map<string, Intl.DateTimeFormat | null>();

const wallFormat = (zone: string): Intl.DateTimeFormat | null => {
    if (!formats.has(zone)) {
        try {
            formats.set(zone, new Intl.DateTimeFormat('en-US', {
                timeZone: zone,
                hourCycle: 'h23',
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric'
            }));
        } catch {
            formats.set(zone, null);
        }
    }
    return formats.get(zone) ?? null;
};

/** An IANA id this browser knows, trimmed; undefined for anything else. */
export const readTimeZone = (value: unknown): string | undefined => {
    const zone = typeof value === 'string' ? value.trim() : '';
    return zone && wallFormat(zone) ? zone : undefined;
};

/** The zone a schedule is saved with: the organization's, else this browser's. */
export const scheduleTimeZone = (organization?: Organization | null): string | undefined =>
    readTimeZone(organization?.address?.timeZone) ?? readTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);

/** `zone` must have passed `readTimeZone`. */
export const wallTime = (instant: number, zone: string): number => {
    const parts = wallFormat(zone)!.formatToParts(new Date(instant));
    const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(entry => entry.type === type)?.value);

    return Date.UTC(part('year'), part('month') - 1, part('day'), part('hour') % 24, part('minute'), part('second'))
        + new Date(instant).getUTCMilliseconds();
};

const offsetAt = (instant: number, zone: string): number => wallTime(instant, zone) - instant;

/**
 * The instant of a wall time, resolved as java.time's `LocalDateTime.atZone` does (the API's `ScheduleRules`):
 * twice on the clock (autumn) → the earlier offset; skipped (spring) → later by the length of the gap.
 */
export const zonedInstant = (wall: number, zone: string): number => {
    const before = offsetAt(wall - DAY_MS, zone);
    const after = offsetAt(wall + DAY_MS, zone);
    const valid = [before, after].filter(offset => offsetAt(wall - offset, zone) === offset);

    return wall - (valid[0] ?? before);
};
